import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'

import Langs, { Localize } from '../../../app/lang/langs'
import { vlearnType } from '../vlearnUi'
import ConfirmDialog from '../../popups/ConfirmDialog'

/**
 * V-Learning — taking the paper.
 *
 * Every question on one scrolling page under a sticky header of numbered jump chips, which
 * is the arrangement `QuizTake.js` established and which the same population of users
 * already knows. Three things about it are deliberately different.
 *
 * ── One submit, not one per question ────────────────────────────────────────────────
 *
 * V-Sécurité posts an answer per question and then a finish call, so a twenty-question quiz
 * is twenty-one requests and a failure on the eleventh leaves a half-graded attempt with
 * nothing to roll it back. Here `onSubmit` is called once with the whole paper and the
 * server grades it in a single pass.
 *
 * ── Submitting with blanks is allowed ───────────────────────────────────────────────
 *
 * V-Sécurité disables submit until every question is answered. That is the wrong gate: the
 * server grades every active question of the quiz regardless, so an unanswered question
 * scores zero and refusing the submit does not save the learner a single point — it only
 * traps them on a question they cannot answer. The confirm dialog names how many are blank,
 * which is the honest version of the same protection.
 *
 * ── The countdown is measured against the server's deadline ─────────────────────────
 *
 * `attempt.expires_at` was written by the database at start with
 * `DATE_ADD(NOW(), INTERVAL ? MINUTE)`. Counting down `time_limit_minutes` from mount
 * instead would restart the clock on every reload, which is precisely the hole
 * V-Sécurité's decorative timer leaves open.
 *
 * @param {object} props
 * @param {object} props.attempt `{ id, attempt_no, started_at, expires_at, resumed, questions }`
 * @param {object} props.quiz `{ title, passing_percent, partial_credit, ... }`
 * @param {Function} props.onSubmit called once with `{ answers, secondsSpent }`
 * @param {Function} props.onAbandon leaves the attempt open and resumable
 */
export default function QuizAttempt({ attempt, quiz, onSubmit, onAbandon }) {
    const lang = useSelector((state) => state.settings.lang)
    const isMobileRedux = useSelector((state) => state.settings.isMobile)

    const theme = useTheme()
    const isSmallQuery = useMediaQuery(theme.breakpoints.down('sm'))
    const isSmall = isMobileRedux === 2 || isSmallQuery

    const questions = useMemo(
        () => (attempt && Array.isArray(attempt.questions) ? attempt.questions : []),
        [attempt])

    /** `{[idQuestion]: idOption}` for single/true_false, `{[idQuestion]: [idOption]}` for multiple. */
    const [selections, setSelections] = useState({})
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    /**
     * A ref as well as the disabled button, because the two protect against different
     * things: the button stops a second click, and the ref stops the expiry effect from
     * firing a submit that the click already started.
     */
    const submittedRef = useRef(false)

    const scrollContainerRef = useRef(null)
    const questionRefs = useRef({})

    // ── The clock ───────────────────────────────────────────────────────────────────

    const startedAtMs = useMemo(() => parseServerTimestamp(attempt && attempt.started_at), [attempt])
    const mountedAtRef = useRef(Date.now())

    /*
     * The deadline is a DURATION from the server, not a comparison of two clocks.
     *
     * `expires_at - Date.now()` needed the viewer's clock and the server's to agree about
     * both the time and the timezone. They do not have to: `seconds_remaining` is computed by
     * the database that owns the deadline, so the only thing measured locally is elapsed time
     * since this component mounted — which is what a stopwatch is for.
     *
     * `expires_at` is still used to render the deadline itself.
     */
    const deadlineAtMs = useMemo(() => {
        const remaining = attempt && attempt.seconds_remaining
        if (remaining === null || typeof remaining === 'undefined') return null
        const seconds = Number(remaining)
        if (!Number.isFinite(seconds)) return null
        return mountedAtRef.current + seconds * 1000
    }, [attempt])

    const [remainingMs, setRemainingMs] = useState(
        () => (deadlineAtMs === null ? null : deadlineAtMs - Date.now()))

    useEffect(() => {
        if (deadlineAtMs === null) {
            setRemainingMs(null)
            return undefined
        }
        const tick = () => setRemainingMs(deadlineAtMs - Date.now())
        tick()
        const timer = setInterval(tick, 1000)
        return () => clearInterval(timer)
    }, [deadlineAtMs])

    // ── Selection ───────────────────────────────────────────────────────────────────

    const setSingle = useCallback((idQuestion, idOption) => {
        setSelections((previous) => ({ ...previous, [idQuestion]: idOption }))
    }, [])

    const toggleMultiple = useCallback((idQuestion, idOption) => {
        setSelections((previous) => {
            const current = Array.isArray(previous[idQuestion]) ? previous[idQuestion] : []
            const next = current.includes(idOption)
                ? current.filter((id) => id !== idOption)
                : [...current, idOption]
            // Emptying a multiple-choice question is left possible on purpose: the learner
            // may want to withdraw an answer they are no longer sure of, and an empty
            // question simply scores zero. V-Sécurité refuses to let the last box go, which
            // means a mis-click can never be fully undone.
            return { ...previous, [idQuestion]: next }
        })
    }, [])

    const isAnswered = useCallback((question) => {
        const value = selections[question.id]
        return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null
    }, [selections])

    const answeredCount = questions.filter(isAnswered).length
    const blankCount = questions.length - answeredCount
    const progressPercent = questions.length ? (answeredCount / questions.length) * 100 : 0

    const scrollToQuestion = useCallback((idQuestion) => {
        const element = questionRefs.current[idQuestion]
        const container = scrollContainerRef.current
        if (!element) return
        if (container) {
            const offset = element.getBoundingClientRect().top
                - container.getBoundingClientRect().top + container.scrollTop - 16
            container.scrollTo({ top: offset, behavior: 'smooth' })
        } else {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
        // Focus follows the jump so a keyboard user lands inside the question they asked
        // for rather than back at the top of the chip strip.
        const firstControl = element.querySelector('input')
        if (firstControl) firstControl.focus({ preventScroll: true })
    }, [])

    // ── Submitting ──────────────────────────────────────────────────────────────────

    const doSubmit = useCallback(() => {
        if (submittedRef.current) return
        submittedRef.current = true
        setSubmitting(true)
        setConfirmOpen(false)

        const answers = questions.map((question) => {
            const value = selections[question.id]
            const selected = Array.isArray(value) ? value : (value === undefined || value === null ? [] : [value])
            return { id_question: question.id, selected_option_ids: selected }
        })

        // Measured from the server's `started_at` rather than from mount, so a resumed
        // attempt reports the time it actually took instead of the time since the reload.
        const anchor = startedAtMs === null ? mountedAtRef.current : startedAtMs
        const secondsSpent = Math.max(0, Math.round((Date.now() - anchor) / 1000))

        /*
         * The submit lock is RELEASED when the request fails.
         *
         * `submittedRef` and `submitting` used to be set and never reset: on any 4xx, 5xx or
         * dropped connection the parent showed a toast and returned, and this component was
         * never told. The button stayed disabled on "Submitting…" forever with no retry, and
         * the only way out — the X — discards `selections`, which is local state. On a
         * twenty-question timed exam that silently destroyed every answer AND consumed the
         * attempt.
         *
         * `onSubmit` therefore resolves false on failure, and a failed submit is fully
         * recoverable: the same answers are still on screen and the button works again.
         */
        if (!onSubmit) return
        Promise.resolve(onSubmit({ answers, secondsSpent })).then((ok) => {
            if (ok === false) {
                submittedRef.current = false
                setSubmitting(false)
            }
        }).catch(() => {
            submittedRef.current = false
            setSubmitting(false)
        })
    }, [onSubmit, questions, selections, startedAtMs])

    /**
     * Auto-submit when the clock runs out.
     *
     * The server grades a late submission and marks the attempt `expired` rather than
     * discarding it, so sending whatever is answered is strictly better than letting the
     * paper lapse unsent: the learner keeps the marks they earned, and the honest status is
     * recorded either way. Doing nothing here would throw away real work for nothing.
     */
    /*
     * Auto-submit only if the clock ran out WHILE THE LEARNER WAS HERE.
     *
     * `remainingMs` is seeded synchronously, so this effect fired on the very first commit
     * when the attempt was already past its deadline — submitting a blank paper before
     * question one was even on screen, and consuming the attempt. Two real routes in:
     * resuming an in-progress attempt whose deadline had passed, and a server timestamp
     * without a timezone being read as browser-local by a client behind the server.
     *
     * So expiry-at-mount is recorded once and treated as a different situation: the paper is
     * shown as closed, and the learner submits it themselves if they want it graded.
     */
    const expiredAtMountRef = useRef(remainingMs !== null && remainingMs <= 0)

    useEffect(() => {
        if (expiredAtMountRef.current) return
        if (remainingMs === null || remainingMs > 0) return
        doSubmit()
    }, [remainingMs, doSubmit])

    const handleSubmitClick = useCallback(() => {
        if (submittedRef.current) return
        if (blankCount > 0) {
            setConfirmOpen(true)
            return
        }
        doSubmit()
    }, [blankCount, doSubmit])

    // ── Render ──────────────────────────────────────────────────────────────────────

    const urgency = timerUrgency(remainingMs)
    /** Points are only shown when they differ: an unweighted paper does not need the noise,
        and a weighted one is unfair without it. */
    const showPoints = useMemo(() => {
        const distinct = new Set(questions.map((question) => Number(question.points) || 1))
        return distinct.size > 1
    }, [questions])

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: isSmall ? '100%' : 'calc(100vh - 180px)',
            borderRadius: isSmall ? 0 : '18px',
            overflow: 'hidden',
            border: isSmall ? 'none' : '1px solid rgba(0,0,0,0.08)',
            backgroundColor: '#f7f8fa'
        }}>

            {/* ── Sticky header ──────────────────────────────────────────────────── */}
            <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 5,
                backgroundColor: 'rgba(255,255,255,0.94)',
                borderBottom: '1px solid rgba(0,0,0,0.07)',
                px: { xs: 1.75, sm: 3 },
                py: { xs: 1.25, sm: 1.75 }
            }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.25 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography component="h2" sx={{
                            ...vlearnType.cardTitle,
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}>
                            {quiz && quiz.title}
                        </Typography>
                        <Typography sx={{ ...vlearnType.meta }}>
                            <Langs
                                str="VLEARN_ATTEMPT_ANSWERED_OF"
                                params={{ answered: answeredCount, total: questions.length }}
                            />
                            {attempt && attempt.attempt_no ? ` · #${attempt.attempt_no}` : ''}
                        </Typography>
                    </Box>

                    {remainingMs !== null ? (
                        <Chip
                            icon={<ScheduleRoundedIcon sx={{ fontSize: '17px !important', color: `${urgency.fg} !important` }} />}
                            label={formatRemaining(remainingMs)}
                            // Polite rather than assertive: a countdown that interrupts a
                            // screen reader every second is worse than no countdown at all.
                            aria-live="polite"
                            aria-label={Localize('VLEARN_ATTEMPT_TIME_LEFT_ARIA', lang)
                                .replace('%time%', formatRemaining(remainingMs))}
                            sx={{
                                fontWeight: 700,
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: '0.82rem',
                                borderRadius: '10px',
                                height: 32,
                                backgroundColor: urgency.bg,
                                color: urgency.fg,
                                border: `1px solid ${urgency.border}`
                            }}
                        />
                    ) : null}

                    <Tooltip title={Localize('VLEARN_ATTEMPT_TOOLTIP_LEAVE', lang)}>
                        <IconButton
                            size="small"
                            onClick={onAbandon}
                            aria-label={Localize('VLEARN_ATTEMPT_TOOLTIP_LEAVE', lang)}
                            sx={{
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: '9px',
                                minWidth: 36, minHeight: 36,
                                color: 'var(--text-muted, #8b92a0)',
                                '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>

                {attempt && attempt.resumed ? (
                    <Typography sx={{ ...vlearnType.meta, color: '#B25E00', fontWeight: 600, mb: 1 }}>
                        <Langs str="VLEARN_ATTEMPT_RESUMED_NOTICE" />
                    </Typography>
                ) : null}

                {/* Beyond a dozen questions the chip strip stops being a shortcut and starts
                    being a wall, so a small screen gets the progress bar instead. */}
                {isSmall && questions.length > 12 ? (
                    <LinearProgress
                        variant="determinate"
                        value={progressPercent}
                        aria-hidden="true"
                        sx={{
                            height: 6, borderRadius: 3, backgroundColor: 'rgba(0,0,0,0.07)',
                            '& .MuiLinearProgress-bar': { backgroundColor: 'var(--orange, rgb(255,144,21))', borderRadius: 3 }
                        }}
                    />
                ) : (
                    <Box
                        component="nav"
                        aria-label={Localize('VLEARN_ATTEMPT_JUMP_NAV_ARIA', lang)}
                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                    >
                        {questions.map((question, index) => {
                            const answered = isAnswered(question)
                            return (
                                <ButtonBase
                                    key={question.id}
                                    onClick={() => scrollToQuestion(question.id)}
                                    aria-label={Localize(answered
                                        ? 'VLEARN_ATTEMPT_JUMP_ANSWERED_ARIA'
                                        : 'VLEARN_ATTEMPT_JUMP_BLANK_ARIA', lang).replace('%n%', index + 1)}
                                    sx={{
                                        width: isSmall ? 34 : 30,
                                        height: isSmall ? 34 : 30,
                                        borderRadius: '9px',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        backgroundColor: answered ? 'var(--orange, rgb(255,144,21))' : 'rgba(0,0,0,0.05)',
                                        color: answered ? '#fff' : 'var(--text-muted, #8b92a0)',
                                        border: `1px solid ${answered ? 'transparent' : 'rgba(0,0,0,0.08)'}`,
                                        transition: 'background-color .18s ease, transform .18s ease',
                                        '&:hover': { transform: 'scale(1.08)' },
                                        '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.55)', outlineOffset: '2px' },
                                        '@media (prefers-reduced-motion: reduce)': {
                                            transition: 'none',
                                            '&:hover': { transform: 'none' }
                                        }
                                    }}
                                >
                                    {index + 1}
                                </ButtonBase>
                            )
                        })}
                    </Box>
                )}
            </Box>

            {/* ── The paper ──────────────────────────────────────────────────────── */}
            <Box
                ref={scrollContainerRef}
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    px: { xs: 1.5, sm: 3 },
                    pt: 2.5,
                    pb: { xs: 4, sm: 3 },
                    WebkitOverflowScrolling: 'touch',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': { background: 'rgba(255,144,21,0.25)', borderRadius: '3px' }
                }}
            >
                {questions.map((question, index) => (
                    <QuestionCard
                        key={question.id}
                        index={index}
                        question={question}
                        answered={isAnswered(question)}
                        showPoints={showPoints}
                        selection={selections[question.id]}
                        onSelectSingle={setSingle}
                        onToggleMultiple={toggleMultiple}
                        registerRef={(element) => { questionRefs.current[question.id] = element }}
                        isSmall={isSmall}
                        lang={lang}
                    />
                ))}
            </Box>

            {/* ── Sticky submit bar ──────────────────────────────────────────────── */}
            <Box sx={{
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'rgba(255,255,255,0.95)',
                borderTop: '1px solid rgba(0,0,0,0.07)',
                px: { xs: 1.75, sm: 3 },
                py: { xs: 1.5, sm: 1.75 },
                pb: { xs: 'calc(12px + env(safe-area-inset-bottom, 0px))', sm: 1.75 },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.75
            }}>
                <Chip
                    label={`${answeredCount} / ${questions.length}`}
                    size="small"
                    sx={{
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        borderRadius: '10px',
                        backgroundColor: blankCount ? 'rgba(0,0,0,0.05)' : 'rgba(58,156,96,0.12)',
                        color: blankCount ? 'var(--text-muted, #8b92a0)' : '#1F7A4C'
                    }}
                />
                <Button
                    variant="contained"
                    disableElevation
                    // Enabled with blanks on purpose — see the docblock. Only the in-flight
                    // submit disables it.
                    disabled={submitting}
                    onClick={handleSubmitClick}
                    fullWidth={isSmall}
                    endIcon={submitting
                        ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                        : <SendRoundedIcon sx={{ fontSize: '18px !important' }} />}
                    sx={{
                        borderRadius: '12px',
                        px: 4,
                        minHeight: 48,
                        textTransform: 'none',
                        fontWeight: 650,
                        fontSize: '0.93rem',
                        backgroundColor: 'var(--orange, rgb(255,144,21))',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#e07d0d' },
                        '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' },
                        '&.Mui-disabled': { backgroundColor: 'rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.34)' }
                    }}
                >
                    <Langs str={submitting ? 'VLEARN_ATTEMPT_BTN_SUBMITTING' : 'VLEARN_ATTEMPT_BTN_SUBMIT'} />
                </Button>
            </Box>

            {/* ── Blank-question confirmation ────────────────────────────────────── */}
            <ConfirmDialog
                open={confirmOpen}
                title={<Langs str="VLEARN_ATTEMPT_CONFIRM_TITLE" />}
                message={<Langs
                    str={blankCount === 1
                        ? 'VLEARN_ATTEMPT_CONFIRM_BODY_ONE'
                        : 'VLEARN_ATTEMPT_CONFIRM_BODY_MANY'}
                    params={{ n: blankCount }}
                />}
                confirmLabel={<Langs str="VLEARN_ATTEMPT_CONFIRM_SUBMIT" />}
                cancelLabel={<Langs str="VLEARN_ATTEMPT_CONFIRM_BACK" />}
                onConfirm={doSubmit}
                onCancel={() => setConfirmOpen(false)}
            />
        </Box>
    )
}

// ── One question ────────────────────────────────────────────────────────────────────

/**
 * A single question card.
 *
 * The question text is the `<FormLabel>` of its own group rather than a heading beside it,
 * so a screen reader announces the question when focus enters the first option instead of
 * reading "radio button, one of four" with no idea what is being asked.
 */
function QuestionCard({
    index, question, answered, showPoints, selection,
    onSelectSingle, onToggleMultiple, registerRef, isSmall, lang
}) {
    const isMultiple = question.type === 'multiple'
    const points = Number(question.points) || 1
    const labelId = `vlearn-question-${question.id}`

    const typeKey = isMultiple
        ? 'VLEARN_ATTEMPT_TYPE_MULTIPLE'
        : question.type === 'true_false'
            ? 'VLEARN_ATTEMPT_TYPE_TRUE_FALSE'
            : 'VLEARN_ATTEMPT_TYPE_SINGLE'

    return (
        <Paper
            ref={registerRef}
            elevation={0}
            sx={{
                mb: 2.25,
                borderRadius: '16px',
                backgroundColor: '#fff',
                border: `1px solid ${answered ? 'rgba(255,144,21,0.30)' : 'rgba(0,0,0,0.08)'}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                overflow: 'hidden',
                transition: 'border-color .2s ease',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
            }}
        >
            {/* `component="div"` rather than a real fieldset: a `<legend>` is only valid as
                the first child of its fieldset, and the question text sits inside a header
                row beside its number chip. The `aria-labelledby` wiring below gives the
                group the same announcement without the invalid markup. */}
            <FormControl component="div" sx={{ width: '100%', display: 'block' }}>
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 1.25, display: 'flex', gap: 1.5 }}>
                    <Chip
                        label={index + 1}
                        size="small"
                        sx={{
                            minWidth: 30, height: 28, borderRadius: '9px', mt: '2px', flexShrink: 0,
                            fontWeight: 700, fontSize: '0.76rem',
                            backgroundColor: answered ? 'rgba(255,144,21,0.14)' : 'rgba(0,0,0,0.05)',
                            color: answered ? '#B25E00' : 'var(--text-muted, #8b92a0)'
                        }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <FormLabel
                            id={labelId}
                            component="div"
                            sx={{
                                display: 'block',
                                fontSize: isSmall ? '0.95rem' : '1rem',
                                fontWeight: 600,
                                lineHeight: 1.45,
                                color: 'var(--text-primary, #111318)',
                                '&.Mui-focused': { color: 'var(--text-primary, #111318)' }
                            }}
                        >
                            {question.question_text}
                        </FormLabel>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                            <Typography sx={{ ...vlearnType.meta }}>
                                <Langs str={typeKey} />
                            </Typography>
                            {showPoints ? (
                                <Chip
                                    size="small"
                                    label={points === 1
                                        ? Localize('VLEARN_ATTEMPT_POINT_ONE', lang)
                                        : Localize('VLEARN_ATTEMPT_POINT_MANY', lang).replace('%n%', points)}
                                    sx={{
                                        height: 20, borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700,
                                        backgroundColor: 'rgba(142, 78, 198, 0.12)', color: '#6D28A8'
                                    }}
                                />
                            ) : null}
                        </Stack>
                    </Box>
                    {answered ? (
                        <CheckCircleOutlineIcon
                            aria-hidden="true"
                            sx={{ color: 'var(--green, rgb(58,156,96))', fontSize: 22, mt: '4px', flexShrink: 0 }}
                        />
                    ) : null}
                </Box>

                <Box sx={{ px: { xs: 1, sm: 2 }, pb: 1.5 }}>
                    {isMultiple ? (
                        <>
                            <FormGroup aria-labelledby={labelId}>
                                {(question.options || []).map((option) => {
                                    const checked = Array.isArray(selection) && selection.includes(option.id)
                                    return (
                                        <FormControlLabel
                                            key={option.id}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={checked}
                                                    onChange={() => onToggleMultiple(question.id, option.id)}
                                                    sx={{
                                                        color: 'rgba(0,0,0,0.24)',
                                                        '&.Mui-checked': { color: 'var(--orange, rgb(255,144,21))' }
                                                    }}
                                                />
                                            }
                                            label={<OptionLabel text={option.option_text} selected={checked} isSmall={isSmall} />}
                                            sx={optionRowSx(checked, isSmall)}
                                        />
                                    )
                                })}
                            </FormGroup>
                            <FormHelperText sx={{ ml: { xs: 1.5, sm: 2 }, ...vlearnType.meta }}>
                                <Langs str="VLEARN_ATTEMPT_MULTIPLE_HINT" />
                            </FormHelperText>
                        </>
                    ) : (
                        <RadioGroup
                            aria-labelledby={labelId}
                            value={selection === undefined || selection === null ? '' : selection}
                            onChange={(event) => onSelectSingle(question.id, Number(event.target.value))}
                        >
                            {(question.options || []).map((option) => {
                                const checked = selection === option.id
                                return (
                                    <FormControlLabel
                                        key={option.id}
                                        value={option.id}
                                        control={
                                            <Radio
                                                size="small"
                                                sx={{
                                                    color: 'rgba(0,0,0,0.24)',
                                                    '&.Mui-checked': { color: 'var(--orange, rgb(255,144,21))' }
                                                }}
                                            />
                                        }
                                        label={<OptionLabel text={option.option_text} selected={checked} isSmall={isSmall} />}
                                        sx={optionRowSx(checked, isSmall)}
                                    />
                                )
                            })}
                        </RadioGroup>
                    )}
                </Box>
            </FormControl>
        </Paper>
    )
}

function OptionLabel({ text, selected, isSmall }) {
    return (
        <Typography sx={{
            fontSize: isSmall ? '0.9rem' : '0.92rem',
            fontWeight: selected ? 600 : 400,
            color: selected ? 'var(--text-primary, #111318)' : 'var(--text-secondary, #4b5263)'
        }}>
            {text}
        </Typography>
    )
}

/**
 * The option row.
 *
 * A selected option gets a filled ground AND a border AND a weight change, because one of
 * the three alone is not enough: colour fails a colour-blind reader, weight alone is easy
 * to miss under time pressure, and someone being assessed has to be certain what they
 * picked.
 */
function optionRowSx(selected, isSmall) {
    return {
        mx: 0,
        mb: 0.4,
        px: isSmall ? 1 : 1.5,
        py: isSmall ? 1 : 0.85,
        borderRadius: '12px',
        minHeight: isSmall ? 48 : 'auto',
        border: `1px solid ${selected ? 'rgba(255,144,21,0.38)' : 'transparent'}`,
        backgroundColor: selected ? 'rgba(255,144,21,0.07)' : 'transparent',
        transition: 'background-color .18s ease, border-color .18s ease',
        '&:hover': { backgroundColor: selected ? 'rgba(255,144,21,0.11)' : 'rgba(0,0,0,0.03)' },
        '&:focus-within': { outline: '2px solid rgba(255,144,21,0.55)', outlineOffset: '1px' },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
    }
}

// ── Local helpers ───────────────────────────────────────────────────────────────────

/**
 * Reads a timestamp the API produced.
 *
 * mysql2 hands back a `Date`, which serialises to ISO-8601 with a `Z`, and a driver
 * configured for strings hands back `YYYY-MM-DD HH:mm:ss`, which Safari refuses outright.
 * Both are accepted here so the countdown is not silently `NaN` on one browser — and null
 * on failure, which renders as "untimed" rather than as an expired attempt.
 */
/**
 * Parses a server timestamp, treating a value with no timezone as UTC.
 *
 * `new Date('2026-09-04 12:00:00')` and `new Date('2026-09-04T12:00:00')` are both read as
 * BROWSER-LOCAL time, so a MySQL datetime serialised without a zone was interpreted in the
 * viewer's zone. A client behind the server's zone therefore saw a brand-new attempt as
 * already expired.
 *
 * This is now only used for DISPLAY — the countdown runs off the server's own
 * `seconds_remaining` duration, which has no timezone to get wrong.
 */
function parseServerTimestamp(value) {
    if (!value) return null
    const raw = String(value).trim()
    // Already carries a zone (a trailing Z or a ±hh:mm offset): trust it.
    const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/.test(raw)
    const candidate = zoned ? raw : `${raw.replace(' ', 'T')}Z`
    const parsed = new Date(candidate)
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime()
    const fallback = new Date(raw)
    return Number.isNaN(fallback.getTime()) ? null : fallback.getTime()
}

/** `mm:ss`, or `h:mm:ss` past the hour. Never negative — zero is what "time is up" looks like. */
function formatRemaining(milliseconds) {
    const total = Math.max(0, Math.floor(Number(milliseconds) / 1000))
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const seconds = total % 60
    const pad = (number) => String(number).padStart(2, '0')
    return hours ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`
}

/**
 * Amber under five minutes, red under one.
 *
 * Informative rather than alarming: no flashing and no animation. Someone with two minutes
 * left needs to know, and needs to keep reading the question.
 */
function timerUrgency(remainingMs) {
    if (remainingMs !== null && remainingMs <= 60 * 1000) {
        return { bg: 'rgba(251,105,98,0.14)', fg: '#B3271F', border: 'rgba(251,105,98,0.35)' }
    }
    if (remainingMs !== null && remainingMs <= 5 * 60 * 1000) {
        return { bg: 'rgba(255,144,21,0.14)', fg: '#B25E00', border: 'rgba(255,144,21,0.35)' }
    }
    return { bg: 'rgba(0,0,0,0.05)', fg: 'var(--text-secondary, #4b5263)', border: 'rgba(0,0,0,0.08)' }
}
